import { useCallback, useEffect, useRef } from 'react';
import { usePersonalWikiId } from './use-personal-wiki-id';
import { request } from 'src/common/request';
import type { WikiParseStatusDTO } from '@next-space/fe-api-idl';
import { create } from 'zustand';

export interface WikiStatus {
  progress: number; // 解析进度百分比 0-100
  status: WikiParseStatusDTO['status']; // 解析状态
  isError: boolean; // 是否解析失败
}

// Wiki状态的全局存储
interface WikiStatusMap {
  [blockId: string]: WikiStatus;
}

interface WikiStatusStore {
  statusMap: WikiStatusMap;
  setStatus: (blockId: string, status: WikiStatus) => void;
  setStatusBatch: (statuses: Record<string, WikiStatus>) => void;
  getStatus: (blockId: string) => WikiStatus | undefined;
  clearStatus: (blockId: string) => void;
  clearAll: () => void;
}

// 创建 zustand store
export const useWikiStatusStore = create<WikiStatusStore>((set, get) => ({
  statusMap: {},
  setStatus: (blockId: string, status: WikiStatus) => {
    set((state) => ({
      statusMap: {
        ...state.statusMap,
        [blockId]: status,
      },
    }));
  },
  setStatusBatch: (statuses: Record<string, WikiStatus>) => {
    set((state) => ({
      statusMap: {
        ...state.statusMap,
        ...statuses,
      },
    }));
  },
  getStatus: (blockId: string) => {
    return get().statusMap[blockId];
  },
  clearStatus: (blockId: string) => {
    set((state) => {
      const newStatusMap = { ...state.statusMap };
      delete newStatusMap[blockId];
      return { statusMap: newStatusMap };
    });
  },
  clearAll: () => {
    set({ statusMap: {} });
  },
}));

// Hook for using the wiki status store with API operations
export const useWikiStatusManager = () => {
  const personalWikiId = usePersonalWikiId();
  const { setStatus, setStatusBatch, getStatus } = useWikiStatusStore();
  const isUpdatingRef = useRef(false);

  // 批量获取 wiki 状态
  const fetchStatusBatch = useCallback(
    async (blockIds: string[]) => {
      if (!personalWikiId || !blockIds.length || isUpdatingRef.current) return;

      // 过滤掉已经是 completed 状态的块，不需要再查询
      const blocksToCheck = blockIds.filter((id) => {
        const currentStatus = getStatus(id)?.status;
        return currentStatus !== 'completed';
      });

      if (!blocksToCheck.length) return;

      isUpdatingRef.current = true;

      try {
        const result = await request.ai.getWikiStatus({
          wikiId: personalWikiId,
          blockIds: blocksToCheck,
        });

        if (result && typeof result === 'object') {
          const newStatuses: Record<string, WikiStatus> = {};

          // 处理返回的每个块的状态
          Object.entries(result).forEach(([blockId, statusData]) => {
            if (statusData && typeof statusData === 'object') {
              const progress = typeof statusData.progress === 'number' ? statusData.progress : 0;
              const statusValue = statusData.status || 'completed';
              const isError = statusValue === 'failed';
              newStatuses[blockId] = {
                progress,
                status: statusValue,
                isError,
              };
            }
          });

          // 批量更新状态
          if (Object.keys(newStatuses).length > 0) {
            setStatusBatch(newStatuses);
          }
        }
      } catch (error) {
        console.error('Failed to fetch wiki status batch:', error);
      } finally {
        isUpdatingRef.current = false;
      }
    },
    [personalWikiId, getStatus, setStatusBatch]
  );

  // 重新解析特定的 wiki
  const reparseWiki = useCallback(
    async (blockId: string) => {
      if (!blockId || !personalWikiId) return;

      try {
        await request.ai.parseWiki({
          blockId,
          wikiId: personalWikiId,
        });

        setStatus(blockId, {
          progress: 0,
          status: 'pending',
          isError: false,
        });
      } catch (error) {
        console.error('Failed to reparse wiki:', error);
      }
    },
    [personalWikiId, setStatus]
  );

  // 定期刷新正在解析中的状态
  useEffect(() => {
    if (!personalWikiId) return;

    const refreshInProgressStatus = async () => {
      // 只查询处于解析中状态的块
      const { statusMap } = useWikiStatusStore.getState();
      const inProgressBlockIds = Object.entries(statusMap)
        .filter(([_, status]) => {
          if (status.status === 'pending') return true;
          return status.status === 'processing' && status.progress < 100;
        })
        .map(([blockId]) => blockId);

      if (inProgressBlockIds.length) {
        await fetchStatusBatch(inProgressBlockIds);
      }
    };

    void refreshInProgressStatus();
    const intervalId = setInterval(() => void refreshInProgressStatus(), 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchStatusBatch, personalWikiId]);

  return {
    fetchStatusBatch,
    reparseWiki,
    getStatus,
  };
};
